import { useState, useMemo, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ChildrenSelector } from "@/components/ChildrenSelector";

import { Users, Plus, Minus, Armchair, Clock, ChevronLeft, User, X, Edit, Baby, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { normalizePhoneForSearch } from "@/lib/clientUtils";
import { calculateEffectiveCapacity } from "@/lib/childrenCapacityUtils";
import { Table, CombinedTable, Reservation } from "@/types/table";
import { CustomerData } from "@/types/client";
import { getBlockedTableIds } from "@/lib/reservationConflictUtils";
import { useEstablishment } from "@/contexts/EstablishmentContext";
import { loadCustomTableOrder, sortByCustomOrder, resolveTableOrder } from "@/lib/tableOrderUtils";

interface WalkInReservationSettings {
  childrenEnabled: boolean;
  childrenCapacityMode: 'full' | 'half' | 'none';
  strollerEnabled: boolean;
}

interface WalkInDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tables: Table[];
  combinedTables: CombinedTable[];
  reservations: Reservation[];
  selectedDate: Date;
  currentTimeSlot?: string;
  reservationDurationMinutes: number;
  bookingMode: "slot" | "time";
  timeSlots: { start: string; end: string }[];
  preSelectedTableId?: string;
  existingClients?: CustomerData[];
  reservationSettings?: WalkInReservationSettings;
  onCreateReservation: (data: {
    customerFirstName: string;
    customerLastName: string;
    customerEmail?: string;
    customerPhone?: string;
    numberOfPeople: number;
    numberOfChildren?: number;
    hasStroller?: boolean;
    tableId?: string;
    combinedTableId?: string;
    startTime: string;
    date: Date;
    internalNotes?: string;
    durationMinutes?: number;
    source: string;
    status?: string;
  }) => Promise<void>;
  onUpdateStatus?: (supabaseId: string, status: "arrivé") => Promise<void>;
}

type Step = "count" | "table";

function adjustTime(time: string, deltaMinutes: number): string {
  const [h, m] = time.split(":").map(Number);
  let total = h * 60 + m + deltaMinutes;
  total = Math.max(0, Math.min(total, 23 * 60 + 45));
  const nh = Math.floor(total / 60);
  const nm = total % 60;
  return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
}

function formatDuration(minutes: number): string {
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
  }
  return `${minutes}min`;
}

function roundToNext15(date: Date): string {
  const minutes = date.getMinutes();
  const roundedMinutes = Math.ceil(minutes / 15) * 15;
  const h = date.getHours() + Math.floor(roundedMinutes / 60);
  const m = roundedMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function parseFreeName(text: string): { firstName: string; lastName: string } {
  const trimmed = text.trim();
  if (!trimmed) return { firstName: "Walk-in", lastName: "" };
  const spaceIdx = trimmed.indexOf(" ");
  if (spaceIdx === -1) return { firstName: trimmed, lastName: "" };
  return { firstName: trimmed.slice(0, spaceIdx), lastName: trimmed.slice(spaceIdx + 1) };
}

export function WalkInDialog({
  open,
  onOpenChange,
  tables,
  combinedTables,
  reservations,
  selectedDate: _selectedDate,
  currentTimeSlot,
  reservationDurationMinutes,
  bookingMode,
  timeSlots,
  preSelectedTableId,
  existingClients = [],
  reservationSettings,
  onCreateReservation,
  onUpdateStatus,
}: WalkInDialogProps) {
  const { establishmentId } = useEstablishment();
  const customTableOrder = useMemo(
    () => resolveTableOrder({ establishmentId, tables, combinedTables }),
    [establishmentId, tables, combinedTables, open]
  );

  // Always use today's date for walk-ins, regardless of dashboard selection
  const today = useMemo(() => new Date(), [open]);

  const [step, setStep] = useState<Step>("count");
  const [guestCount, setGuestCount] = useState(2);
  const [numberOfChildren, setNumberOfChildren] = useState(0);
  const [hasStroller, setHasStroller] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditingTime, setIsEditingTime] = useState(false);

  // Client identification state
  const [selectedClient, setSelectedClient] = useState<CustomerData | null>(null);
  const [freeTextName, setFreeTextName] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Editable time & duration
  const defaultTime = useMemo(() => roundToNext15(new Date()), []);
  const [selectedTime, setSelectedTime] = useState(defaultTime);
  const [selectedDuration, setSelectedDuration] = useState(reservationDurationMinutes);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        clientInputRef.current &&
        !clientInputRef.current.contains(e.target as Node)
      ) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reset on open
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setStep("count");
      setGuestCount(2);
      setNumberOfChildren(0);
      setHasStroller(false);
      setIsCreating(false);
      setIsEditingTime(false);
      setSelectedTime(roundToNext15(new Date()));
      setSelectedDuration(reservationDurationMinutes);
      setSelectedClient(null);
      setFreeTextName("");
      setShowClientDropdown(false);
    }
    onOpenChange(newOpen);
  };

  // Filtered clients for autocomplete
  const filteredClients = useMemo(() => {
    if (!freeTextName.trim() || selectedClient) return [];
    const query = freeTextName.toLowerCase();
    const queryNorm = normalizePhoneForSearch(query);
    return existingClients
      .filter((c) => {
        const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
        const email = c.email?.toLowerCase() || "";
        const phoneNorm = normalizePhoneForSearch(c.phone || '');
        return fullName.includes(query) || email.includes(query) ||
          (queryNorm.length >= 2 && phoneNorm.includes(queryNorm));
      })
      .slice(0, 6);
  }, [freeTextName, existingClients, selectedClient]);

  // Build customer data for payload
  const getCustomerPayload = () => {
    if (selectedClient) {
      return {
        customerFirstName: selectedClient.firstName,
        customerLastName: selectedClient.lastName,
        customerEmail: selectedClient.email || undefined,
        customerPhone: selectedClient.phone || undefined,
      };
    }
    if (freeTextName.trim()) {
      const { firstName, lastName } = parseFreeName(freeTextName);
      return { customerFirstName: firstName, customerLastName: lastName };
    }
    return { customerFirstName: "Walk-in", customerLastName: "" };
  };

  // Find current time slot based on selected time
  const activeTimeSlot = useMemo(() => {
    if (currentTimeSlot) return currentTimeSlot;
    const [h, m] = selectedTime.split(":").map(Number);
    const selectedMinutes = h * 60 + m;
    const slot = timeSlots.find((ts) => {
      const [sh, sm] = ts.start.split(":").map(Number);
      const [eh, em] = ts.end.split(":").map(Number);
      return selectedMinutes >= sh * 60 + sm && selectedMinutes < eh * 60 + em;
    });
    return slot?.start || selectedTime;
  }, [currentTimeSlot, timeSlots, selectedTime]);

  // Effective capacity considering children mode
  const effectiveCapacity = useMemo(() => {
    const capacityMode = reservationSettings?.childrenCapacityMode ?? 'full';
    return calculateEffectiveCapacity(guestCount - numberOfChildren, numberOfChildren, capacityMode);
  }, [guestCount, numberOfChildren, reservationSettings?.childrenCapacityMode]);

  // Available tables filtered by capacity and selected time
  const { availableTables, unavailableTables } = useMemo(() => {
    const blockedIds = getBlockedTableIds(
      reservations,
      combinedTables,
      today,
      bookingMode === "slot" ? activeTimeSlot : selectedTime,
      {
        bookingMode,
        reservationDurationMinutes: selectedDuration,
      }
    );

    const activeTables = tables.filter((t) => t.isActive);
    const activeCombined = combinedTables.filter((ct) => ct.isActive);

    const freeTables = sortByCustomOrder(
      activeTables.filter((t) => !blockedIds.has(t.id) && t.capacity >= effectiveCapacity),
      customTableOrder,
      (a, b) => a.capacity - b.capacity
    );

    const freeCombined = sortByCustomOrder(
      activeCombined.filter((ct) => !blockedIds.has(ct.id) && ct.totalCapacity >= effectiveCapacity),
      customTableOrder,
      (a, b) => a.totalCapacity - b.totalCapacity
    );

    const freeTableIds = new Set(freeTables.map((t) => t.id));
    const freeCombinedIds = new Set(freeCombined.map((ct) => ct.id));

    const unavailSingle = sortByCustomOrder(
      activeTables
        .filter((t) => !freeTableIds.has(t.id))
        .map((t) => ({
          ...t,
          reason: blockedIds.has(t.id) ? ("occupied" as const) : ("capacity" as const),
        })),
      customTableOrder,
      (a, b) => a.capacity - b.capacity
    );

    const unavailCombined = sortByCustomOrder(
      activeCombined
        .filter((ct) => !freeCombinedIds.has(ct.id))
        .map((ct) => ({
          ...ct,
          reason: blockedIds.has(ct.id) ? ("occupied" as const) : ("capacity" as const),
        })),
      customTableOrder,
      (a, b) => a.totalCapacity - b.totalCapacity
    );

    return {
      availableTables: { tables: freeTables, combinedTables: freeCombined },
      unavailableTables: { tables: unavailSingle, combinedTables: unavailCombined },
    };
  }, [tables, combinedTables, reservations, today, activeTimeSlot, selectedTime, effectiveCapacity, bookingMode, selectedDuration, customTableOrder]);

  const startTimeForPayload = bookingMode === "time" ? selectedTime : activeTimeSlot;

  const handleSeatNow = async (tableId: string, isCombined: boolean) => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const customer = getCustomerPayload();
      await onCreateReservation({
        ...customer,
        numberOfPeople: guestCount - numberOfChildren,
        numberOfChildren,
        hasStroller,
        tableId: isCombined ? undefined : tableId,
        combinedTableId: isCombined ? tableId : undefined,
        startTime: startTimeForPayload,
        date: today,
        durationMinutes: selectedDuration,
        source: "walk_in",
      });
      handleOpenChange(false);
    } catch {
      // Error handled by parent
    } finally {
      setIsCreating(false);
    }
  };

  const handleWaitlist = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const customer = getCustomerPayload();
      await onCreateReservation({
        ...customer,
        numberOfPeople: guestCount - numberOfChildren,
        numberOfChildren,
        hasStroller,
        startTime: startTimeForPayload,
        date: today,
        source: "walk_in",
        status: "en_attente",
      });
      handleOpenChange(false);
    } catch {
      // Error handled by parent
    } finally {
      setIsCreating(false);
    }
  };

  const totalAvailable = availableTables.tables.length + availableTables.combinedTables.length;
  const totalUnavailable = unavailableTables.tables.length + unavailableTables.combinedTables.length;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md max-h-[85vh] min-h-0 flex flex-col overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            📍 Walk-in — Aujourd'hui
          </DialogTitle>
          <DialogDescription>
            {step === "count"
              ? `${today.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} — Combien de personnes ?`
              : `${today.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} — Choisissez une table`}
          </DialogDescription>
          <Badge variant="info" className="text-[10px] w-fit mt-1">📌 Aujourd'hui uniquement</Badge>
        </DialogHeader>

        {step === "count" && (
          <div className="space-y-5">
            {/* Quick guest count grid */}
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                <Button
                  key={n}
                  variant={guestCount === n ? "default" : "outline"}
                  className={cn(
                    "h-14 text-lg font-bold",
                    guestCount === n && "ring-2 ring-primary ring-offset-2"
                  )}
                  onClick={() => {
                    setGuestCount(n);
                    if (numberOfChildren >= n) setNumberOfChildren(Math.max(0, n - 1));
                  }}
                >
                  {n}
                </Button>
              ))}
              <Button
                variant={guestCount > 7 ? "default" : "outline"}
                className={cn(
                  "h-14 text-lg font-bold",
                  guestCount > 7 && "ring-2 ring-primary ring-offset-2"
                )}
                onClick={() => setGuestCount(guestCount > 7 ? guestCount : 8)}
                aria-label={guestCount > 7 ? `${guestCount} personnes` : "Plus de personnes"}
              >
                {guestCount > 7 ? guestCount : <Plus className="w-5 h-5" />}
              </Button>
            </div>
            {guestCount > 7 && (
              <div className="flex items-center justify-between gap-2 h-11 rounded-md border border-primary/30 bg-primary/10 px-2">
                <Button
                  type="button"
                  variant="default"
                  size="icon"
                  className="h-9 w-9 shrink-0 rounded-full"
                  onClick={() => {
                    const next = Math.max(1, guestCount - 1);
                    setGuestCount(next);
                    if (numberOfChildren >= next) setNumberOfChildren(Math.max(0, next - 1));
                  }}
                  aria-label="Diminuer le nombre de personnes"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="text-sm font-semibold tabular-nums">
                  <span className="text-lg">{guestCount}</span> personnes
                </span>
                <Button
                  type="button"
                  variant="default"
                  size="icon"
                  className="h-9 w-9 shrink-0 rounded-full"
                  onClick={() => setGuestCount(Math.min(50, guestCount + 1))}
                  aria-label="Augmenter le nombre de personnes"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Children & Stroller (conditional) */}
            {(reservationSettings?.childrenEnabled || reservationSettings?.strollerEnabled) && (
              <div className="flex items-center gap-4 rounded-md border border-border bg-muted/30 px-3 py-2">
                {reservationSettings?.childrenEnabled && (
                  <ChildrenSelector
                    value={numberOfChildren}
                    onChange={(v) => setNumberOfChildren(v)}
                    max={guestCount - 1}
                    compact
                  />
                )}
                {reservationSettings?.strollerEnabled && (
                  <div className="flex items-center gap-2 ml-auto">
                    <label htmlFor="walkin-stroller" className="text-xs text-muted-foreground whitespace-nowrap">
                      Poussette
                    </label>
                    <Switch
                      id="walkin-stroller"
                      checked={hasStroller}
                      onCheckedChange={setHasStroller}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Time & Duration controls */}
            <div className="grid grid-cols-2 gap-3">
              {/* Time control */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Heure d'arrivée
                </label>
                <div className="flex items-center h-12 rounded-md border border-input bg-muted/30">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-full w-10 rounded-r-none shrink-0"
                    onClick={() => setSelectedTime(adjustTime(selectedTime, -15))}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  {isEditingTime ? (
                    <input
                      type="time"
                      value={selectedTime}
                      onChange={(e) => e.target.value && setSelectedTime(e.target.value)}
                      onBlur={() => setIsEditingTime(false)}
                      onKeyDown={(e) => e.key === "Enter" && setIsEditingTime(false)}
                      autoFocus
                      className="w-full text-center text-lg font-bold tabular-nums bg-transparent border-none outline-none [&::-webkit-calendar-picker-indicator]:hidden"
                    />
                  ) : (
                    <span
                      className="flex-1 text-center text-lg font-bold tabular-nums cursor-pointer hover:text-primary transition-colors"
                      onClick={() => setIsEditingTime(true)}
                      title="Tap pour ajuster"
                    >
                      {selectedTime}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-full w-10 rounded-l-none shrink-0"
                    onClick={() => setSelectedTime(adjustTime(selectedTime, 15))}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {bookingMode === "slot" && (
                  <p className="text-[10px] text-muted-foreground text-center mt-1 truncate">
                    {timeSlots.find((ts) => {
                      const [h, m] = selectedTime.split(":").map(Number);
                      const sel = h * 60 + m;
                      const [sh, sm] = ts.start.split(":").map(Number);
                      const [eh, em] = ts.end.split(":").map(Number);
                      return sel >= sh * 60 + sm && sel < eh * 60 + em;
                    })
                      ? `${(() => {
                          const ts = timeSlots.find((ts) => {
                            const [h, m] = selectedTime.split(":").map(Number);
                            const sel = h * 60 + m;
                            const [sh, sm] = ts.start.split(":").map(Number);
                            const [eh, em] = ts.end.split(":").map(Number);
                            return sel >= sh * 60 + sm && sel < eh * 60 + em;
                          })!;
                          return `${ts.start.slice(0, 5)} – ${ts.end.slice(0, 5)}`;
                        })()}`
                      : "⚠ Hors créneau"}
                  </p>
                )}
              </div>

              {/* Duration control */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Durée
                </label>
                <div className="flex items-center h-12 rounded-md border border-input bg-muted/30">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-full w-10 rounded-r-none shrink-0"
                    onClick={() => setSelectedDuration(Math.max(15, selectedDuration - 15))}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <div className="flex-1 text-center">
                    <span className="text-lg font-bold tabular-nums">
                      {formatDuration(selectedDuration)}
                    </span>
                    {selectedDuration === reservationDurationMinutes && (
                      <span className="block text-[10px] text-muted-foreground -mt-0.5">par défaut</span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-full w-10 rounded-l-none shrink-0"
                    onClick={() => setSelectedDuration(Math.min(240, selectedDuration + 15))}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Client identification field */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                Client
              </label>
              <div className="relative">
                {selectedClient ? (
                  <div className="flex items-center h-10 rounded-md border border-input bg-muted/30 px-3 gap-2">
                    <User className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="flex-1 text-sm font-medium truncate">
                      {selectedClient.firstName} {selectedClient.lastName}
                    </span>
                    {selectedClient.guestStatus === 'vip' && (
                      <Badge variant="default" className="text-[10px] px-1 py-0 shrink-0">VIP</Badge>
                    )}
                    {selectedClient.isBlacklisted && (
                      <Badge variant="destructive" className="text-[10px] px-1 py-0 shrink-0">Blacklist</Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => {
                        setSelectedClient(null);
                        setFreeTextName("");
                        setTimeout(() => clientInputRef.current?.focus(), 50);
                      }}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      ref={clientInputRef}
                      type="text"
                      value={freeTextName}
                      onChange={(e) => {
                        setFreeTextName(e.target.value);
                        setShowClientDropdown(true);
                      }}
                      onFocus={() => freeTextName.trim() && setShowClientDropdown(true)}
                      placeholder="Nom ou téléphone"
                      className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                  </div>
                )}

                {/* Autocomplete dropdown */}
                {showClientDropdown && !selectedClient && freeTextName.trim() && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border border-border bg-popover shadow-md max-h-48 overflow-y-auto"
                  >
                    {filteredClients.map((client) => (
                      <button
                        key={client.id || `${client.firstName}-${client.lastName}`}
                        className="w-full text-left px-3 py-2 hover:bg-accent/50 transition-colors flex items-center gap-2 text-sm"
                        onClick={() => {
                          setSelectedClient(client);
                          setFreeTextName(`${client.firstName} ${client.lastName}`);
                          setShowClientDropdown(false);
                        }}
                      >
                        <div className="flex-1 min-w-0">
                          <span className="font-medium">
                            {client.firstName} {client.lastName}
                          </span>
                          {client.phone && (
                            <span className="text-muted-foreground ml-2 text-xs">
                              📞 {client.phone}
                            </span>
                          )}
                        </div>
                        {client.guestStatus === 'vip' && (
                          <Badge variant="default" className="text-[10px] px-1 py-0 shrink-0">VIP</Badge>
                        )}
                        {client.isBlacklisted && (
                          <Badge variant="destructive" className="text-[10px] px-1 py-0 shrink-0">⚠</Badge>
                        )}
                      </button>
                    ))}
                    {/* Option to use free text name */}
                    <button
                      className="w-full text-left px-3 py-2 hover:bg-accent/50 transition-colors text-sm border-t border-border text-muted-foreground"
                      onClick={() => setShowClientDropdown(false)}
                    >
                      Utiliser « {freeTextName.trim()} »
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2">
              <Button
                size="lg"
                className="w-full text-base"
                onClick={() => setStep("table")}
              >
                <Armchair className="w-5 h-5 mr-2" />
                Assigner une table
                {totalAvailable > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {totalAvailable} dispo
                  </Badge>
                )}
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="w-full text-base"
                onClick={handleWaitlist}
                disabled={isCreating}
              >
                <Clock className="w-5 h-5 mr-2" />
                Ajouter en attente
              </Button>
            </div>
          </div>
        )}

        {step === "table" && (
          <div className="flex flex-col min-h-0 flex-1 overflow-hidden space-y-3">
            <div className="flex items-center justify-between shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep("count")}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Retour
              </Button>
              <button
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                onClick={() => setStep("count")}
              >
                <User className="w-3.5 h-3.5" />
                <span className="font-medium truncate max-w-[150px]">
                  {selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : freeTextName.trim() || "Walk-in"}
                </span>
                <span>· {guestCount} pers.</span>
                <Edit className="w-3 h-3" />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="space-y-3 pr-1">
                {/* Available tables */}
                {totalAvailable > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {availableTables.tables.map((table) => (
                      <Card
                        key={table.id}
                        className={cn(
                          "p-3 cursor-pointer hover:bg-accent/50 transition-colors active:scale-95",
                          table.isTerrace && "border-primary/30"
                        )}
                        onClick={() => handleSeatNow(table.id, false)}
                      >
                        <p className="font-semibold text-sm truncate">
                          {table.name}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <Users className="w-3 h-3" />
                          <span>{table.capacity} places</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {table.isTerrace && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0">
                              Terrasse
                            </Badge>
                          )}
                          {!table.isBookable && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 text-muted-foreground">
                              Hors résa.
                            </Badge>
                          )}
                          {reservationSettings?.childrenEnabled && !table.allowsChildren && numberOfChildren > 0 && (
                            <Badge variant="warning" className="text-[10px] px-1 py-0">
                              Pas d'enfants
                            </Badge>
                          )}
                          {reservationSettings?.strollerEnabled && !table.allowsStroller && hasStroller && (
                            <Badge variant="warning" className="text-[10px] px-1 py-0">
                              Pas de poussette
                            </Badge>
                          )}
                        </div>
                      </Card>
                    ))}
                    {availableTables.combinedTables.map((ct) => (
                      <Card
                        key={ct.id}
                        className={cn(
                          "p-3 cursor-pointer hover:bg-accent/50 transition-colors active:scale-95",
                          ct.isTerrace && "border-primary/30"
                        )}
                        onClick={() => handleSeatNow(ct.id, true)}
                      >
                        <p className="font-semibold text-sm truncate">
                          {ct.name}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <Users className="w-3 h-3" />
                          <span>{ct.totalCapacity} places</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {ct.isTerrace && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0">
                              Terrasse
                            </Badge>
                          )}
                          {!ct.isBookable && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 text-muted-foreground">
                              Hors résa.
                            </Badge>
                          )}
                          {reservationSettings?.childrenEnabled && !ct.allowsChildren && numberOfChildren > 0 && (
                            <Badge variant="warning" className="text-[10px] px-1 py-0">
                              Pas d'enfants
                            </Badge>
                          )}
                          {reservationSettings?.strollerEnabled && !ct.allowsStroller && hasStroller && (
                            <Badge variant="warning" className="text-[10px] px-1 py-0">
                              Pas de poussette
                            </Badge>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}

                {totalAvailable === 0 && (
                  <div className="text-center py-4 text-muted-foreground">
                    <Armchair className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="font-medium text-sm">Aucune table disponible</p>
                    <p className="text-xs">
                      pour {guestCount} personne{guestCount > 1 ? "s" : ""}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={handleWaitlist}
                      disabled={isCreating}
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Mettre en attente
                    </Button>
                  </div>
                )}

                {/* Unavailable tables */}
                {totalUnavailable > 0 && (
                  <>
                    <div className="flex items-center gap-2 pt-1">
                      <div className="h-px flex-1 bg-border" />
                      <span className="text-[11px] font-medium text-muted-foreground shrink-0">
                        Autres tables
                      </span>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {unavailableTables.tables.map((table) => (
                        <Card
                          key={table.id}
                          className={cn(
                            "p-3 cursor-pointer hover:bg-accent/30 transition-colors active:scale-95 opacity-60",
                            table.isTerrace && "border-primary/20"
                          )}
                          onClick={() => handleSeatNow(table.id, false)}
                        >
                          <p className="font-semibold text-sm truncate">
                            {table.name}
                          </p>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                            <Users className="w-3 h-3" />
                            <span>{table.capacity} places</span>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            <Badge
                              variant={table.reason === "occupied" ? "warning" : "secondary"}
                              className="text-[10px] px-1 py-0"
                            >
                              {table.reason === "occupied" ? "Occupée" : "Sous-capacité"}
                            </Badge>
                            {table.isTerrace && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0">
                                Terrasse
                              </Badge>
                            )}
                            {!table.isBookable && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 text-muted-foreground">
                                Hors résa.
                              </Badge>
                            )}
                            {reservationSettings?.childrenEnabled && !table.allowsChildren && numberOfChildren > 0 && (
                              <Badge variant="warning" className="text-[10px] px-1 py-0">
                                Pas d'enfants
                              </Badge>
                            )}
                            {reservationSettings?.strollerEnabled && !table.allowsStroller && hasStroller && (
                              <Badge variant="warning" className="text-[10px] px-1 py-0">
                                Pas de poussette
                              </Badge>
                            )}
                          </div>
                        </Card>
                      ))}
                      {unavailableTables.combinedTables.map((ct) => (
                        <Card
                          key={ct.id}
                          className={cn(
                            "p-3 cursor-pointer hover:bg-accent/30 transition-colors active:scale-95 opacity-60",
                            ct.isTerrace && "border-primary/20"
                          )}
                          onClick={() => handleSeatNow(ct.id, true)}
                        >
                          <p className="font-semibold text-sm truncate">
                            {ct.name}
                          </p>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                            <Users className="w-3 h-3" />
                            <span>{ct.totalCapacity} places</span>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            <Badge
                              variant={ct.reason === "occupied" ? "warning" : "secondary"}
                              className="text-[10px] px-1 py-0"
                            >
                              {ct.reason === "occupied" ? "Occupée" : "Sous-capacité"}
                            </Badge>
                            {ct.isTerrace && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0">
                                Terrasse
                              </Badge>
                            )}
                            {!ct.isBookable && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 text-muted-foreground">
                                Hors résa.
                              </Badge>
                            )}
                            {reservationSettings?.childrenEnabled && !ct.allowsChildren && numberOfChildren > 0 && (
                              <Badge variant="warning" className="text-[10px] px-1 py-0">
                                Pas d'enfants
                              </Badge>
                            )}
                            {reservationSettings?.strollerEnabled && !ct.allowsStroller && hasStroller && (
                              <Badge variant="warning" className="text-[10px] px-1 py-0">
                                Pas de poussette
                              </Badge>
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
